import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

/**
 * AnimatedLogo - Logo Motivya avec animations et taille adaptative
 * @param {string} size - 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'hero'
 * @param {boolean} animate - Active les animations
 * @param {boolean} linkTo - Si défini, wrap le logo dans un Link
 * @param {string} className - Classes additionnelles
 * @param {string} variant - 'default' | 'glow' | 'pulse' | 'float' | 'halo'
 * @param {boolean} whiteHalo - Ajoute un halo blanc derrière le logo
 */
export default function AnimatedLogo({ 
  size = 'md', 
  animate = true, 
  linkTo = null,
  className = '',
  variant = 'default',
  whiteHalo = false,
  testId = 'logo'
}) {
  // Tailles responsives adaptatives
  const sizeClasses = {
    xs: 'h-8 sm:h-9 md:h-10',
    sm: 'h-10 sm:h-12 md:h-14',
    md: 'h-12 sm:h-14 md:h-16',
    lg: 'h-14 sm:h-16 md:h-20 lg:h-24',
    xl: 'h-16 sm:h-20 md:h-24 lg:h-28',
    hero: 'h-48 sm:h-64 md:h-80 lg:h-96 xl:h-[28rem]',
  };

  // Animations selon la variante
  const animations = {
    default: {
      initial: { opacity: 0, scale: 0.9 },
      animate: { opacity: 1, scale: 1 },
      transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] },
      whileHover: { scale: 1.05, transition: { duration: 0.2 } },
    },
    glow: {
      initial: { opacity: 0, scale: 0.9 },
      animate: { 
        opacity: 1, 
        scale: 1,
        filter: ['drop-shadow(0 0 8px rgba(59, 173, 229, 0.3))', 'drop-shadow(0 0 20px rgba(59, 173, 229, 0.6))', 'drop-shadow(0 0 8px rgba(59, 173, 229, 0.3))']
      },
      transition: { 
        duration: 0.5,
        filter: { duration: 3, repeat: Infinity, ease: 'easeInOut' }
      },
      whileHover: { scale: 1.05, filter: 'drop-shadow(0 0 30px rgba(59, 173, 229, 0.8))' },
    },
    halo: {
      initial: { opacity: 0, scale: 0.9 },
      animate: { 
        opacity: 1, 
        scale: 1,
      },
      transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] },
      whileHover: { scale: 1.05 },
    },
    pulse: {
      initial: { opacity: 0, scale: 0.9 },
      animate: { 
        opacity: 1, 
        scale: [1, 1.02, 1],
      },
      transition: { 
        duration: 0.5,
        scale: { duration: 2, repeat: Infinity, ease: 'easeInOut' }
      },
      whileHover: { scale: 1.08 },
    },
    float: {
      initial: { opacity: 0, y: 20 },
      animate: { 
        opacity: 1, 
        y: [0, -8, 0],
      },
      transition: { 
        duration: 0.6,
        y: { duration: 4, repeat: Infinity, ease: 'easeInOut' }
      },
      whileHover: { scale: 1.05, y: -15 },
    },
  };

  const currentAnimation = animate ? animations[variant] : {};

  // Halo blanc pour meilleure visibilité
  const haloStyle = whiteHalo || variant === 'halo' ? {
    filter: 'drop-shadow(0 0 30px rgba(255, 255, 255, 0.9)) drop-shadow(0 0 60px rgba(255, 255, 255, 0.6)) drop-shadow(0 0 90px rgba(255, 255, 255, 0.3))'
  } : {};

  const LogoImage = (
    <motion.div 
      className="relative inline-flex items-center justify-center"
      data-testid={testId}
    >
      {/* Halo blanc derrière */}
      {(whiteHalo || variant === 'halo') && (
        <div 
          className={`absolute inset-0 ${sizeClasses[size]} w-auto rounded-full blur-2xl bg-white/40`}
          style={{ transform: 'scale(1.5)' }}
        />
      )}
      <motion.img
        src="/logo.png"
        alt="Motivya"
        className={`
          ${sizeClasses[size]} 
          w-auto object-contain relative z-10
          ${className}
        `}
        style={haloStyle}
        {...currentAnimation}
      />
    </motion.div>
  );

  if (linkTo) {
    return (
      <Link to={linkTo} className="inline-flex items-center">
        {LogoImage}
      </Link>
    );
  }

  return LogoImage;
}

/**
 * LogoWithTagline - Logo avec tagline animée
 */
export function LogoWithTagline({ size = 'hero', animate = true }) {
  return (
    <div className="text-center">
      <AnimatedLogo size={size} animate={animate} variant="halo" whiteHalo={true} />
      <motion.p
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3, duration: 0.5 }}
        className="text-lg sm:text-xl text-white/60 mt-4 font-light tracking-wide"
      >
        Ready. Set. <span className="text-[var(--brand-cyan)] font-semibold">Go.</span>
      </motion.p>
    </div>
  );
}

/**
 * HeaderLogo - Logo optimisé pour les headers avec couleurs inversées
 * Utilise un filtre invert pour meilleure visibilité sur fond sombre
 */
export function HeaderLogo({ linkTo = '/', size = 'md', testId = 'header-logo', inverted = false }) {
  const sizeClasses = {
    sm: 'h-10 sm:h-12 md:h-14',
    md: 'h-12 sm:h-14 md:h-16',
    lg: 'h-14 sm:h-16 md:h-20',
  };
  
  return (
    <Link to={linkTo} className="inline-flex items-center group" data-testid={testId}>
      <motion.div className="relative">
        {/* Halo subtil derrière le logo header */}
        <motion.div 
          className="absolute inset-0 rounded-full bg-cyan-500/20 blur-xl opacity-0 group-hover:opacity-100"
          style={{ transform: 'scale(1.8)' }}
          animate={{ 
            scale: [1.5, 1.8, 1.5],
            opacity: [0.2, 0.4, 0.2]
          }}
          transition={{ 
            duration: 3, 
            repeat: Infinity, 
            ease: 'easeInOut' 
          }}
        />
        <motion.img
          src="/logo.png"
          alt="Motivya"
          className={`
            ${sizeClasses[size] || sizeClasses.md}
            w-auto object-contain relative z-10
            transition-all duration-300
            ${inverted ? 'brightness-0 invert' : ''}
            group-hover:drop-shadow-[0_0_25px_rgba(59,173,229,0.6)]
          `}
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.4 }}
          whileHover={{ scale: 1.08 }}
          whileTap={{ scale: 0.98 }}
        />
      </motion.div>
    </Link>
  );
}
